import { Delete, Add, ExpandMore } from "@mui/icons-material";
import "dayjs/locale/ar-sa";
import "dayjs/locale/am";
import {
  Accordion,
  AccordionDetails,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { FormikProps } from "formik";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikTextField } from "../../../components/Layout/FormikTextField";
import { StyledAccordionSummary } from "../../../styles/componentStyled";

import { StyledTableRow, StyledTableCell } from "../../../styles/tableStyles";
import {
  CountriesList,
  ProfessionalTypes,
  ExperienceYears,
  Employee,
  CookingExperience,
  LanguageExperience,
  WorkExperience,
  Skills,
} from "../types/processTypes";
import { useAppSelector } from "../../../app/hooks";
import { selectPreference } from "../../preferences/preferencesSlice";

interface ExperienceTabProps {
  formikData: FormikProps<Employee>;
  columns?: 3 | 4;
  foreignExperienceOnly?: boolean;
}
export const Experience = ({
  formikData,
  columns = 4,
  foreignExperienceOnly = false,
}: ExperienceTabProps) => {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const theme = useTheme();
  const { locale } = useAppSelector(selectPreference);

  const [country, setCountry] = useState(CountriesList.saudi_arabia);
  const [showAddExperience, setShowAddExperience] = useState(false);
  const [profession, setProfession] = useState(ProfessionalTypes.housemaid);
  const [years, setYears] = useState(ExperienceYears.two);
  const onChangeC = (value: ChangeEvent<HTMLInputElement>) => {
    setCountry(value.target.value as CountriesList);
  };
  const onChangeP = (value: ChangeEvent<HTMLInputElement>) => {
    setProfession(value.target.value as ProfessionalTypes);
  };
  const onChangeY = (value: ChangeEvent<HTMLInputElement>) => {
    setYears(value.target.value as ExperienceYears);
  };

  const addExperience = () => {
    if (formikData.values.workExperience)
      formikData.setFieldValue("workExperience", [
        ...(formikData.values.workExperience as [WorkExperience]),
        { country, profession, years },
      ]);
    else
      formikData.setFieldValue("workExperience", [
        { country, profession, years },
      ]);
    setShowAddExperience(false);
  };
  const addForeignExperience = () => {
    setShowAddExperience(true);
  };
  const removeExperience = (id: string) => {
    formikData.setFieldValue(
      "workExperience",
      formikData.values.workExperience?.filter((w) => w._id !== id),
    );
  };
  const fieldCol = columns === 3 ? 4 : 3;
  const skillsCol = columns === 3 ? 4 : 5;
  const remarksCol = columns === 3 ? 8 : 7;
  return (
    <Box>
      <Accordion
        elevation={2}
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
        expanded
      >
        {/* <StyledAccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography variant="h6" component="div">
            Skills and Experience
          </Typography>
        </StyledAccordionSummary> */}
        <AccordionDetails sx={{ p: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}>
          {!foreignExperienceOnly && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item md={fieldCol} xs={12}>
              <FormikTextField
                formikKey="education.arabicLanguage"
                select
                label={t("arabic")}
              >
                {Object.keys(LanguageExperience).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te((option as string).toLowerCase())}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Grid>

            <Grid item md={fieldCol} xs={12}>
              <FormikTextField
                formikKey="education.englishLanguage"
                select
                label={t("english")}
              >
                {Object.keys(LanguageExperience).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te((option as string).toLowerCase())}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Grid>
            <Grid item md={fieldCol} xs={12}>
              <FormikTextField
                formikKey="education.amharicLanguage"
                select
                label="Amharic"
              >
                {Object.keys(LanguageExperience).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te((option as string).toLowerCase())}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Grid>
            <Grid item md={fieldCol} xs={12}>
              <FormikTextField
                formikKey="education.cooking"
                select
                label={te("cooking")}
              >
                {Object.keys(CookingExperience).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te((option as string).toLowerCase())}
                  </MenuItem>
                ))}
              </FormikTextField>
            </Grid>
            <Grid item md={skillsCol} xs={12}>
              <FormikTextField
                formikKey="education.skills"
                select
                label="Skills"
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  defaultValue: [],
                  size: "small",
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 250,
                      },
                    },

                    variant: "menu",
                  },
                }}
              >
                {Object.keys(Skills)
                  .filter((option) =>
                    [
                      "baby_sitting",
                      "washing_ironing_clothes",
                      "dusting_cleaning",
                    ].includes(option),
                  )
                  .map((option) => (
                    <MenuItem
                      key={option}
                      value={option as string}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.action.selected,
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      {te(option)}
                    </MenuItem>
                  ))}
              </FormikTextField>
            </Grid>
            <Grid item md={remarksCol} xs={12}>
              <FormikTextField
                formikKey="education.remarks"
                label="Other Skills"
              />
            </Grid>
          </Grid>
          )}
          <Grid container justifyContent="flex-start">
            {formikData.values.workExperience &&
              formikData.values.workExperience.length > 0 && (
                <TableContainer component={Paper} sx={{ mb: 1 }}>
                  <Table size="small" aria-label="simple table">
                    <TableHead>
                      <StyledTableRow>
                        <StyledTableCell>{t("country")}</StyledTableCell>
                        <StyledTableCell>{t("profession")}</StyledTableCell>
                        <StyledTableCell>{t("forYears")}</StyledTableCell>
                        <StyledTableCell>Actions</StyledTableCell>
                      </StyledTableRow>
                    </TableHead>
                    <TableBody>
                      {formikData.values.workExperience &&
                        formikData.values.workExperience.map((row, index) => (
                          <StyledTableRow key={index}>
                            <StyledTableCell>
                              {te(row.country as string)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {te(row.profession as string)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {te(row.years as string)}
                            </StyledTableCell>

                            <StyledTableCell>
                              <IconButton
                                color="secondary"
                                onClick={() =>
                                  removeExperience(row._id as string)
                                }
                                size="large"
                              >
                                <Delete />
                              </IconButton>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
          </Grid>
          <Grid container spacing={2}>
            {!showAddExperience && (
              <Grid item xs={12}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={addForeignExperience}
                  size="small"
                >
                  <Add /> Add Foreign Experience
                </Button>
              </Grid>
            )}
            {showAddExperience && (
              <>
                <Grid item md={fieldCol} xs={12}>
                  <TextField
                    id="experienceCountry"
                    select
                    defaultValue="Ethiopia"
                    label={t("country")}
                    onChange={onChangeC}
                    value={country}
                    fullWidth
                    size="small"
                  >
                    {Object.keys(CountriesList).map((option) => (
                      <MenuItem key={option} value={option as string}>
                        {te(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item md={fieldCol} xs={12}>
                  <TextField
                    id="experienceProfession"
                    select
                    defaultValue="Housemaid"
                    label="Profession"
                    onChange={onChangeP}
                    value={profession}
                    fullWidth
                    size="small"
                  >
                    {Object.keys(ProfessionalTypes).map((option) => (
                      <MenuItem key={option} value={option as string}>
                        {te(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item md={fieldCol} xs={12}>
                  <TextField
                    id="experienceYears"
                    select
                    defaultValue="Two"
                    label={t("forYears")}
                    onChange={onChangeY}
                    value={years}
                    fullWidth
                    size="small"
                  >
                    {Object.keys(ExperienceYears).map((option) => (
                      <MenuItem key={option} value={option as string}>
                        {te(option.toLowerCase())}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item md={fieldCol} xs={12}>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={addExperience}
                    size="large"
                  >
                    <Add /> Add
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
